import { Vacancy } from 'src/app/models';

/**
 * MPS-19: vagas ADICIONAIS da embarcação (ex.: catamarã ocupando duas vagas). A vaga principal continua no campo
 * `vacancy`; estas vão em `additionalVacancies` no PUT /secure/boat/ e são lidas de GET /secure/boat/{id}/additional-vacancies.
 */
export interface AdditionalVacancyOption {
  label: string;
  value: number;
}

/**
 * Opções do seletor: as mesmas vagas oferecidas para a vaga principal (livres/para embarcação), mais as adicionais que a
 * embarcação já tem (que não aparecem como livres, porque ela mesma as ocupa), sem a vaga principal escolhida. Ordenadas
 * pelo código.
 */
export function buildAdditionalVacancyOptions(
  base: { label: string; value: Vacancy }[] | null | undefined,
  current: Vacancy[] | null | undefined,
  mainVacancyId: number | null | undefined
): AdditionalVacancyOption[] {
  const byId = new Map<number, string>();
  (base || [])
    .filter((option) => option?.value?.id != null)
    .forEach((option) => byId.set(option.value.id, option.value.code ?? option.label));
  (current || [])
    .filter((vacancy) => vacancy?.id != null && !byId.has(vacancy.id))
    .forEach((vacancy) => byId.set(vacancy.id, vacancy.code));
  if (mainVacancyId != null) {
    byId.delete(mainVacancyId);
  }
  return Array.from(byId.entries())
    .map(([id, code]) => ({ label: code, value: id }))
    .sort((a, b) => (a.label || '').localeCompare(b.label || '', 'pt-BR', { numeric: true }));
}

/** Seleção que continua válida depois de mudar a vaga principal ou as opções (sem principal = nenhuma). */
export function keepSelectedAdditionalVacancies(
  selected: number[] | null | undefined,
  options: AdditionalVacancyOption[],
  mainVacancyId: number | null | undefined
): number[] {
  if (mainVacancyId == null) {
    return [];
  }
  const allowed = new Set(options.map((option) => option.value));
  return (selected || []).filter((id) => allowed.has(id));
}

/** Corpo enviado ao backend: `[{id, code}]`; vazio quando a embarcação não é recorrente ou não tem vaga principal. */
export function toAdditionalVacancies(
  selected: number[] | null | undefined,
  options: AdditionalVacancyOption[],
  mainVacancyId: number | null | undefined,
  recurrent: boolean
): Vacancy[] {
  if (!recurrent || mainVacancyId == null) {
    return [];
  }
  const codes = new Map(options.map((option) => [option.value, option.label]));
  return (selected || [])
    .filter((id) => id !== mainVacancyId && codes.has(id))
    .map((id) => ({ id, code: codes.get(id) } as Vacancy));
}
